import cloneDeep from 'lodash.clonedeep';

/*
 * Helper function that lets you set multiple values on a proxy state at once.
 * Without replacing the entire object, and therefore without losing the proxy.
 */
export function setStoreValues<T extends object>(
  proxyState: T,
  values: Partial<T> | null | undefined,
) {
  if (values == null || typeof values !== 'object') {
    return;
  }
  const resetObj = cloneDeep(values) as Record<string, unknown>;
  const target = proxyState as unknown as Record<string, unknown>;
  Object.keys(resetObj).forEach((key) => {
    const srcVal = resetObj[key];
    const dstVal = target[key];

    if (isPrimitive(srcVal)) {
      target[key] = srcVal;
      return;
    }

    if (Array.isArray(srcVal)) {
      if (Array.isArray(dstVal)) {
        reconcileArray(dstVal as unknown[], srcVal as unknown[]);
      } else {
        // Different shape; replace this branch only
        target[key] = cloneDeep(srcVal);
      }
      return;
    }

    if (isPlainObject(srcVal)) {
      if (isPlainObject(dstVal)) {
        mergeLeaves(
          dstVal as Record<string, unknown>,
          srcVal as Record<string, unknown>,
        );
      } else {
        // No existing object to merge into; set once
        target[key] = cloneDeep(srcVal);
      }
      return;
    }

    // Fallback for non-plain objects (e.g., Date, Map): set directly
    target[key] = cloneDeep(srcVal);
  });
}

function isPrimitive(
  value: unknown,
): value is null | undefined | string | number | boolean | bigint | symbol {
  return (
    value === null ||
    value === undefined ||
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    typeof value === 'bigint' ||
    typeof value === 'symbol'
  );
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== 'object') return false;
  if (Array.isArray(value)) return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

function mergeLeaves(
  target: Record<string, unknown>,
  source: Record<string, unknown>,
) {
  Object.keys(source).forEach((key) => {
    const sVal = source[key];
    const dVal = target[key];

    if (isPrimitive(sVal)) {
      target[key] = sVal;
      return;
    }

    if (Array.isArray(sVal)) {
      if (Array.isArray(dVal)) {
        reconcileArray(dVal as unknown[], sVal as unknown[]);
      } else {
        target[key] = cloneDeep(sVal);
      }
      return;
    }

    if (isPlainObject(sVal)) {
      if (isPlainObject(dVal)) {
        mergeLeaves(
          dVal as Record<string, unknown>,
          sVal as Record<string, unknown>,
        );
      } else {
        target[key] = cloneDeep(sVal);
      }
      return;
    }

    // Non-plain objects (Date, Map, Set, etc.)
    target[key] = cloneDeep(sVal);
  });
}

function reconcileArray(targetArr: unknown[], sourceArr: unknown[]) {
  // Resize to match
  if (targetArr.length > sourceArr.length) {
    targetArr.length = sourceArr.length;
  }
  for (let i = 0; i < sourceArr.length; i += 1) {
    const sVal = sourceArr[i];
    const dVal = targetArr[i];

    if (isPrimitive(sVal)) {
      targetArr[i] = sVal;
      continue;
    }

    if (Array.isArray(sVal)) {
      if (Array.isArray(dVal)) {
        reconcileArray(dVal as unknown[], sVal as unknown[]);
      } else {
        targetArr[i] = cloneDeep(sVal);
      }
      continue;
    }

    if (isPlainObject(sVal)) {
      if (isPlainObject(dVal)) {
        mergeLeaves(
          dVal as Record<string, unknown>,
          sVal as Record<string, unknown>,
        );
      } else {
        targetArr[i] = cloneDeep(sVal);
      }
      continue;
    }

    targetArr[i] = cloneDeep(sVal);
  }
}
